import { Button, LoaderWrapper } from '@fl/cmsch-fe-library';
import React, { FC, memo, useMemo } from 'react';
import { Opt } from 'ts-opt';
import { CriteriaDefinition } from 'api/gen/CriteriaDefinition';
import { useOurTranslation } from 'app/translations';
import { Ant } from 'common/ant';
import { useForm } from 'utils/forms';
import { actionPanelRowGutter, formSectionsRowGutter, initialValues, labelCol, wrapperCol, submitButtonLabel, rightFormSide, leftFormSide, middleFormArea } from './params-helpers';
import { matingParamsSelectionFormName, MatingLoadingId } from '../../types';
import styles from './styles.sass';
interface Props {
  /** Criteria definitions used to build the sliders. */
  criteriaDefinitions: Opt<Array<CriteriaDefinition>>;
  /** Minimum female age used by the left-side form section. */
  minimumFemaleAgeInMonths: number | null;
  /** Loading flags for the parameter form. */
  loading: Array<MatingLoadingId>;
  /** Whether the criteria list modal has any entries. */
  criteriaListsEmpty: boolean;
  /** Whether there are animals available in the current session. */
  animalsAvailableInSession: boolean;
  /** Handles form submission. */
  onSubmit(): void;
  /** Opens or closes the criteria list save modal. */
  toggleSaveCriteriaListVisibility(): void;
  /** Opens or closes the criteria list loader modal. */
  toggleLoadCriteriaListVisibility(): void;
  /** Opens or closes the overview modal for saved criteria lists. */
  toggleCriteriaListOverviewVisibility(): void;
}

// eslint-disable-next-line max-lines-per-function
const MatingParamsSelectionFormBase: FC<Props> = props => {
  const {
    criteriaDefinitions,
    minimumFemaleAgeInMonths,
    loading,
    criteriaListsEmpty,
    animalsAvailableInSession,
    onSubmit,
    toggleSaveCriteriaListVisibility,
    toggleLoadCriteriaListVisibility,
    toggleCriteriaListOverviewVisibility
  } = props;
  const {
    Form,
    Fields,
    valid,
    submitting,
    reset
  } = useForm({
    form: matingParamsSelectionFormName,
    initialValues,
    onSubmit
  }, {
    layout: 'horizontal',
    labelAlign: 'left',
    labelCol,
    wrapperCol,
    labelWrap: true
  });
  const {
    t,
    tCommon
  } = useOurTranslation('mating');
  const parmsForm = useMemo(() => <Ant.Row gutter={formSectionsRowGutter}>
            {leftFormSide(criteriaDefinitions, Fields, t, minimumFemaleAgeInMonths)}
            {middleFormArea(criteriaDefinitions, Fields, t)}
            {rightFormSide(criteriaDefinitions, Fields, t)}
        </Ant.Row>, [criteriaDefinitions, Fields, minimumFemaleAgeInMonths, t]);
  const calculateScoreButton = useMemo(() => <Ant.Col xs={24} lg={6}>
            <Button type="submit" disabled={!valid || !animalsAvailableInSession || submitting} tooltip={submitButtonLabel(valid, animalsAvailableInSession, t)} loading={submitting} visuals="primary" block>
                {t('calculateScore')}
            </Button>
        </Ant.Col>, [animalsAvailableInSession, submitting, t, valid]);
  const loadCriteriaFromListButton = useMemo(() => <Ant.Col xs={24} lg={6}>
            <Button onClick={toggleLoadCriteriaListVisibility} loading={loading.includes('criteriaLists')} disabled={criteriaListsEmpty} tooltip={criteriaListsEmpty ? t('noListAvailable') : undefined} block>
                {t('loadCriteriaFromList')}
            </Button>
        </Ant.Col>, [criteriaListsEmpty, loading, t, toggleLoadCriteriaListVisibility]);
  const saveCriteriaListButton = useMemo(() => <Ant.Col xs={24} lg={6}>
            <Button onClick={toggleSaveCriteriaListVisibility} disabled={!valid} tooltip={!valid ? t('formIsInvalid') : undefined} block>
                {t('saveCriteriaList')}
            </Button>
        </Ant.Col>, [t, toggleSaveCriteriaListVisibility, valid]);
  const manageSavedListsButton = useMemo(() => <Ant.Col xs={24} lg={6}>
            <Button onClick={toggleCriteriaListOverviewVisibility} loading={loading.includes('criteriaLists')} block>
                {t('manageSavedLists')}
            </Button>
        </Ant.Col>, [loading, t, toggleCriteriaListOverviewVisibility]);
  return useMemo(() => <span className={styles.container}>
            <Form>
                {submitting && <LoaderWrapper loading background stretch size="large" text={t('scoreIsBeingCalculated')} />}
                <div className={styles.buttonWrapper}>
                    <Button onClick={reset} danger>{tCommon('resetSettings')}</Button>
                </div>
                {parmsForm}
                <Ant.Row justify="center" gutter={actionPanelRowGutter} className="mt-3 mb-3">
                    {calculateScoreButton}
                    {loadCriteriaFromListButton}
                    {saveCriteriaListButton}
                    {manageSavedListsButton}
                </Ant.Row>
            </Form>
        </span>, [Form, submitting, t, tCommon, reset, parmsForm, calculateScoreButton, loadCriteriaFromListButton, saveCriteriaListButton, manageSavedListsButton]);
};

/**
 * Memoized calculation-parameter form for the mating workflow.
 *
 * @param props Criteria definitions and modal handlers.
 * @returns The parameter selection form.
 */
export const MatingParamsSelectionForm = memo(MatingParamsSelectionFormBase);